import 'prismjs/components/prism-markup-templating';

import Markdown from '@wandb/weave/common/components/Markdown';
import {isMarkdown} from '@wandb/weave/common/util/markdown';
import _ from 'lodash';
import React, {useMemo, useState} from 'react';

import {TargetBlank} from '../../../../../../common/util/links';
import {Button} from '../../../../../Button';
import {CodeEditor} from '../../../../../CodeEditor';
import {
  CustomWeaveTypeDispatcher,
  CustomWeaveTypeProjectContext,
} from '../../typeViews/CustomWeaveTypeDispatcher';
import {usePlaygroundContext} from '../PlaygroundPage/PlaygroundContext';
import {LargeMarkdownFallback} from './LargeMarkdownFallback';
import {MarkdownContainer} from './MarkdownContainer';
import {useMarkdownSetting} from './MarkdownSettingContext';
import {StructuredOutputsMessagePart} from './StructuredOutputsMessagePart';
import {ToolCalls} from './ToolCalls';
import {ContentMessage, ImageUrl, MessagePart, ToolCall} from './types';

type MessagePanelPartProps = {
  value: MessagePart;
  isStructuredOutput?: boolean;
  showCursor?: boolean;
  role?: string;
  reasoningContent?: string;
  onThinkingExpandedChange?: (expanded: boolean) => void;
};

type CursorAnimatedProps = {
  show: boolean;
};

const CursorAnimated = ({show}: CursorAnimatedProps) => {
  if (!show) return null;
  return (
    <span className="cursor-blink -mb-[2px] ml-[4px] inline-block h-[12px] w-[12px] rounded-full bg-gold-500" />
  );
};

// Helper function to check if a message part is a ContentMessage with CustomWeaveType
const isContentMessage = (value: any): value is ContentMessage => {
  if (!value || typeof value !== 'object') return false;
  if (!('type' in value)) return false;

  // Check if it has the content type pattern (e.g., audio, video, image)
  const contentKey = value.type;
  if (!(contentKey in value)) return false;

  const content = value[contentKey];
  return content?._type === 'CustomWeaveType';
};

type ThinkingPartProps = {
  content: string;
  index: number;
  isExpanded: boolean;
  showCursor: boolean;
  isLastPart: boolean;
  onToggleExpanded: (index: number) => void;
  isMarkdownEnabled: boolean;
  isPlayground: boolean;
  showMarkdownInChatView: boolean;
  largeMarkdownThreshold: number;
};

const ThinkingPart = ({
  content,
  index,
  isExpanded,
  showCursor,
  isLastPart,
  onToggleExpanded,
  isMarkdownEnabled,
  isPlayground,
  showMarkdownInChatView,
  largeMarkdownThreshold,
}: ThinkingPartProps) => {
  return (
    <div>
      <Button
        variant="ghost"
        size="small"
        className="mb-8"
        endIcon={isExpanded ? 'chevron-up' : 'chevron-down'}
        onClick={() => onToggleExpanded(index)}>
        Thinking
      </Button>
      {isExpanded && (
        <div className="mb-8 rounded bg-moon-100 p-16">
          {((isMarkdownEnabled && isPlayground) ||
            (!isPlayground && showMarkdownInChatView)) &&
          isMarkdown(content) ? (
            content.length > largeMarkdownThreshold ? (
              <LargeMarkdownFallback content={content} />
            ) : (
              <MarkdownContainer>
                <Markdown content={content} shouldEjectFromTailwind={false} />
              </MarkdownContainer>
            )
          ) : (
            <span className="whitespace-break-spaces italic text-moon-600">
              {content}
            </span>
          )}
          <CursorAnimated show={showCursor && isLastPart} />
        </div>
      )}
    </div>
  );
};

type TextPartProps = {
  content: string;
  showCursor: boolean;
  isLastPart: boolean;
};

const TextPart = ({content, showCursor, isLastPart}: TextPartProps) => {
  return (
    <span className="whitespace-break-spaces">
      {!showCursor && isLastPart ? content.trim() : content}
      <CursorAnimated show={showCursor && isLastPart} />
    </span>
  );
};

export const MessagePanelPart = ({
  value,
  isStructuredOutput,
  showCursor = false,
  role,
  reasoningContent,
  onThinkingExpandedChange,
}: MessagePanelPartProps) => {
  const {showMarkdownInChatView} = useMarkdownSetting();
  const {isMarkdownEnabled, isPlayground} = usePlaygroundContext();
  const projectContext = React.useContext(CustomWeaveTypeProjectContext);
  const [expandedThinking, setExpandedThinking] = useState<{
    [key: number]: boolean;
  }>({});

  const LARGE_MARKDOWN_THRESHOLD = 50000; // characters

  const parts = useMemo(() => {
    if (typeof value !== 'string') {
      return [];
    }

    const parts: Array<{type: 'thinking' | 'text'; content: string}> = [];

    // Add reasoning content if available (only for non-user messages)
    if (reasoningContent && role !== 'user') {
      parts.push({type: 'thinking', content: reasoningContent});
    }

    // Add the main text content
    if (value) {
      parts.push({type: 'text', content: value});
    }

    return parts.length > 0 ? parts : [{type: 'text' as const, content: value}];
  }, [role, value, reasoningContent]);

  // Single definition to render reasoning header above non-string parts
  const ReasoningHeader = () => {
    if (!_.isString(reasoningContent) || role === 'user') return null;
    const idx = 0;
    const isExpanded = expandedThinking[idx] ?? showCursor;
    return (
      <ThinkingPart
        content={reasoningContent}
        index={idx}
        isExpanded={isExpanded}
        showCursor={showCursor}
        isLastPart={false}
        onToggleExpanded={i => {
          const newState = {...expandedThinking, [i]: !isExpanded};
          const anyExpanded = Object.values(newState).some(Boolean);
          setExpandedThinking(newState);
          onThinkingExpandedChange?.(anyExpanded);
        }}
        isMarkdownEnabled={isMarkdownEnabled}
        isPlayground={isPlayground}
        showMarkdownInChatView={showMarkdownInChatView}
        largeMarkdownThreshold={LARGE_MARKDOWN_THRESHOLD}
      />
    );
  };

  if (typeof value === 'string') {
    if (isStructuredOutput) {
      try {
        const reformat = JSON.stringify(JSON.parse(value), null, 2);
        return <StructuredOutputsMessagePart value={reformat} />;
        // If it is a structured output, but parsing fails, just return the raw string
        // This happens when streaming the object, and the current chunk is not valid JSON.
      } catch (error) {}
    }

    const lastPartIndex = parts.length - 1;

    return (
      <div>
        {parts.map((part, index) => {
          const isLastPart = index === lastPartIndex;
          if (part.type === 'thinking') {
            const isExpanded = expandedThinking[index] ?? showCursor;
            return (
              <ThinkingPart
                key={index}
                content={part.content}
                index={index}
                isExpanded={isExpanded}
                showCursor={showCursor}
                isLastPart={isLastPart}
                onToggleExpanded={idx => {
                  const newState = {...expandedThinking, [idx]: !isExpanded};
                  const anyExpanded = Object.values(newState).some(Boolean);
                  setExpandedThinking(newState);
                  onThinkingExpandedChange?.(anyExpanded);
                }}
                isMarkdownEnabled={isMarkdownEnabled}
                isPlayground={isPlayground}
                showMarkdownInChatView={showMarkdownInChatView}
                largeMarkdownThreshold={LARGE_MARKDOWN_THRESHOLD}
              />
            );
          }
          if (
            ((isMarkdownEnabled && isPlayground) ||
              (!isPlayground && showMarkdownInChatView)) &&
            isMarkdown(part.content)
          ) {
            const isLarge = part.content.length > LARGE_MARKDOWN_THRESHOLD;
            return (
              <div key={index}>
                {isLarge ? (
                  <LargeMarkdownFallback content={part.content} />
                ) : (
                  <MarkdownContainer>
                    <Markdown
                      content={part.content}
                      shouldEjectFromTailwind={false}
                    />
                  </MarkdownContainer>
                )}
              </div>
            );
          }
          return (
            <TextPart
              key={index}
              content={part.content}
              showCursor={showCursor}
              isLastPart={isLastPart}
            />
          );
        })}
      </div>
    );
  }
  if (value.type === 'text' && 'text' in value) {
    return (
      <>
        <ReasoningHeader />
        <div className="whitespace-break-spaces">{value.text}</div>
      </>
    );
  }
  if (value.type === 'image_url' && 'image_url' in value && value.image_url) {
    const imageUrl = value.image_url as ImageUrl;
    const {url} = imageUrl;
    return (
      <div>
        <ReasoningHeader />
        {!url?.startsWith('data:') && (
          <div className="text-xs">
            <TargetBlank href={url}>{url}</TargetBlank>
          </div>
        )}
        <div>
          <img src={url} alt="" />
        </div>
      </div>
    );
  }
  if (value.type === 'tool_use' && 'name' in value && 'id' in value) {
    const toolCall: ToolCall = {
      id: (value.id as string) || '',
      type: value.type,
      function: {
        name: (value.name as string) || '',
        arguments: JSON.stringify(value.input) || '',
      },
    };
    return (
      <>
        <ReasoningHeader />
        <ToolCalls toolCalls={[toolCall]} />
      </>
    );
  }

  if (value.type === 'tool_result' && 'content' in value) {
    // value.content can be a string or an array of content blocks
    const contentArray = Array.isArray(value.content)
      ? value.content
      : [value.content];
    return (
      <>
        <ReasoningHeader />
        {contentArray.map((content, index) => {
          const stringContent =
            _.isObject(content) && 'text' in content ? content.text : content;
          try {
            const jsonContent = JSON.stringify(
              JSON.parse(stringContent as string),
              null,
              2
            );
            return (
              <CodeEditor
                key={index}
                language="json"
                value={jsonContent}
                readOnly
              />
            );
          } catch (error) {
            return (
              <span key={index} className="whitespace-break-spaces">
                {stringContent}
              </span>
            );
          }
        })}
      </>
    );
  }

  // Special handling for audio content to display linked transcript from wrapper
  if (value.type === 'audio' && 'audio' in value && value['audio']) {
    return (
      <div className="my-2">
        <ReasoningHeader />
        {/* Transcript above audio, with divider between */}
        {'transcript' in value && value['transcript'] && (
          <div className="whitespace-break-spaces">{value['transcript']}</div>
        )}
        {'transcript' in value && 'audio' in value && (
          <div className="mb-8 border-t border-moon-250" />
        )}
        {'audio' in value && (
          <CustomWeaveTypeDispatcher
            data={value.audio}
            entity={projectContext?.entity}
            project={projectContext?.project}
          />
        )}
      </div>
    );
  } else if (isContentMessage(value)) {
    const content = value[value.type];
    return (
      <div className="my-2" style={{height: '400px', minHeight: '200px'}}>
        <ReasoningHeader />
        <CustomWeaveTypeDispatcher
          data={content}
          entity={projectContext?.entity}
          project={projectContext?.project}
        />
      </div>
    );
  }
  return null;
};
